import { useEffect, useState, useCallback } from 'react'
import { isKenoConfig, type Game } from '@nexbit/types'
import {
  apiAdminGetGames,
  apiAdminUpdateGame,
  ApiRequestError,
  type AdminUpdateGameBody,
} from '@/lib/api'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { AlertCircle, ChevronDown, ChevronUp, Loader2, Check } from 'lucide-react'
import { formatCurrency } from '@/lib/utils'
import { useT } from '@/contexts/language'

function GameEditForm({
  game,
  onSave,
  onCancel,
}: {
  game: Game
  onSave: (g: Game) => void
  onCancel: () => void
}) {
  const t = useT()
  const [minBet, setMinBet] = useState(game.minBet)
  const [maxBet, setMaxBet] = useState(game.maxBet)
  const [roundDuration, setRoundDuration] = useState(String(game.roundDurationSeconds))
  const [betClose, setBetClose] = useState(String(game.betCloseBeforeSeconds))
  const [payHigh, setPayHigh] = useState(String(game.config.payoutRates.high))
  const [payLow, setPayLow] = useState(String(game.config.payoutRates.low))
  const [payOdd, setPayOdd] = useState(String(game.config.payoutRates.odd))
  const [payEven, setPayEven] = useState(String(game.config.payoutRates.even))
  const [threshold, setThreshold] = useState(String(game.config.winConditions.highThreshold))
  const [isActive, setIsActive] = useState(game.isActive)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function handleSave() {
    setSaving(true)
    setErr(null)
    try {
      const body: AdminUpdateGameBody = {
        minBet,
        maxBet,
        roundDurationSeconds: parseInt(roundDuration, 10),
        betCloseBeforeSeconds: parseInt(betClose, 10),
        config: {
          payoutRates: {
            high: parseFloat(payHigh),
            low: parseFloat(payLow),
            odd: parseFloat(payOdd),
            even: parseFloat(payEven),
          },
          winConditions: { highThreshold: parseInt(threshold, 10) },
        },
        isActive,
      }
      onSave(await apiAdminUpdateGame(game.id, body))
    } catch (e) {
      setErr(e instanceof ApiRequestError ? e.message : t.games.saveError)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="p-4 bg-secondary/20 border-t border-border space-y-3">
      {err && (
        <div className="flex items-center gap-2 p-2 border border-destructive/40 rounded-[var(--radius-sm)] bg-card">
          <AlertCircle className="h-3.5 w-3.5 text-destructive shrink-0" />
          <p className="font-mono text-xs text-destructive">{err}</p>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {(
          [
            { label: t.games.minBet,         value: minBet,       set: setMinBet,       inputType: 'text' },
            { label: t.games.maxBet,         value: maxBet,       set: setMaxBet,       inputType: 'text' },
            { label: t.games.roundSec,       value: roundDuration,set: setRoundDuration,inputType: 'number' },
            { label: t.games.closeBeforeSec, value: betClose,     set: setBetClose,     inputType: 'number' },
          ] satisfies { label: string; value: string; set: (v: string) => void; inputType: string }[]
        ).map(({ label, value, set, inputType }) => (
          <div key={label} className="flex flex-col gap-1">
            <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              {label}
            </Label>
            <Input
              type={inputType}
              value={value}
              onChange={(e) => set(e.target.value)}
              className="font-mono text-xs h-8"
            />
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {(
          [
            { label: t.games.highX,         value: payHigh,   set: setPayHigh },
            { label: t.games.lowX,          value: payLow,    set: setPayLow },
            { label: t.games.oddX,          value: payOdd,    set: setPayOdd },
            { label: t.games.evenX,         value: payEven,   set: setPayEven },
            { label: t.games.highThreshold, value: threshold, set: setThreshold },
          ] satisfies { label: string; value: string; set: (v: string) => void }[]
        ).map(({ label, value, set }) => (
          <div key={label} className="flex flex-col gap-1">
            <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              {label}
            </Label>
            <Input
              type="number"
              step="0.01"
              value={value}
              onChange={(e) => set(e.target.value)}
              className="font-mono text-xs h-8"
            />
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between pt-1">
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            className="accent-primary h-4 w-4"
          />
          <span className="font-mono text-xs text-muted-foreground">{t.games.isActive}</span>
        </label>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onCancel}
            className="h-7 text-xs"
            disabled={saving}
          >
            {t.common.cancel}
          </Button>
          <Button
            size="sm"
            onClick={() => void handleSave()}
            className="h-7 text-xs gap-1.5"
            disabled={saving}
          >
            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
            {t.common.save}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default function AdminGamesPage() {
  const t = useT()
  const [games, setGames] = useState<Game[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      // KENO tables have their own config shape; this page edits price games only.
      setGames((await apiAdminGetGames()).filter((g) => !isKenoConfig(g.config)))
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t.common.error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  function handleSave(updated: Game) {
    setGames((prev) => prev.map((g) => (g.id === updated.id ? updated : g)))
    setEditingId(null)
  }

  return (
    <div className="p-4 md:p-6 max-w-4xl">
      <div className="mb-5">
        <h1 className="text-lg font-semibold tracking-tight">{t.games.title}</h1>
        <p className="text-sm text-muted-foreground mt-0.5">{t.games.subtitle}</p>
      </div>

      {error && (
        <div
          className="flex items-center gap-2 p-3 border border-destructive/40 rounded-[var(--radius)] bg-card mb-4"
          role="alert"
        >
          <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
          <p className="font-mono text-xs text-destructive">{error}</p>
        </div>
      )}

      <div className="border border-border rounded-[var(--radius)] bg-card overflow-hidden shadow-[var(--shadow-sm)]">
        <div className="px-4 py-2.5 bg-secondary flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-wider text-secondary-foreground">
            {t.games.config}
          </span>
          {!loading && (
            <span className="font-mono text-xs text-secondary-foreground/60">{games.length} {t.games.unit}</span>
          )}
        </div>

        {loading ? (
          <div className="divide-y divide-border">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex items-center gap-4 px-4 py-3">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-24" />
                <Skeleton className="ml-auto h-5 w-14" />
                <Skeleton className="h-7 w-16" />
              </div>
            ))}
          </div>
        ) : games.length === 0 ? (
          <div className="py-10 text-center">
            <p className="text-sm text-muted-foreground">{t.games.noGames}</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {games.map((game) => (
              <div key={game.id}>
                <div className="flex items-center gap-3 px-4 py-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-mono text-sm font-semibold text-foreground truncate">
                      {game.name}
                    </p>
                    <p className="font-mono text-[10px] text-muted-foreground mt-0.5">
                      {t.games.metaRound} {game.roundDurationSeconds} {t.games.secShort} ·{' '}
                      {t.games.metaClose} {game.betCloseBeforeSeconds} {t.games.secShort} ·{' '}
                      {formatCurrency(game.minBet)}–{formatCurrency(game.maxBet)} ·{' '}
                      {t.games.metaPayout} ×{game.config.payoutRates.high}
                    </p>
                  </div>
                  <Badge
                    variant={game.isActive ? 'success' : 'outline'}
                    className="text-[10px] shrink-0"
                  >
                    {game.isActive ? t.common.active : t.common.inactive}
                  </Badge>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 font-mono text-xs gap-1.5 shrink-0"
                    onClick={() => setEditingId(editingId === game.id ? null : game.id)}
                  >
                    {editingId === game.id ? (
                      <ChevronUp className="h-3 w-3" />
                    ) : (
                      <ChevronDown className="h-3 w-3" />
                    )}
                    {editingId === game.id ? t.common.close : t.common.edit}
                  </Button>
                </div>
                {editingId === game.id && (
                  <GameEditForm
                    game={game}
                    onSave={handleSave}
                    onCancel={() => setEditingId(null)}
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
